/**
 * A link to another record as a thin pill with a small icon for what it is (a line, a place, an
 * organisation, ...), as in "Connected to". Used wherever a value is a link to a record.
 */

import { Link } from 'react-router-dom';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { setting, type ViewPropertyShape } from '@utils/viewShapes.utils';
import { ERA } from '../page';
import { uriText } from './values';
import type { WidgetContext } from './types';
import styles from '../EntityTemplate.module.scss';

/** RINF (era:) links come from describe, without shape rows: their icons, until a RINF shape has them. */
const RINF_ICON: Record<string, string> = {
  [`${ERA}isPartOf`]: 'network',
  [`${ERA}infrastructureManager`]: 'org',
  [`${ERA}contactLineSystem`]: 'power',
};

/** The icon of a row's pills: its dview:icon, else a RINF predicate's. */
export function chipIcon(row: ViewPropertyShape | null | undefined, predicate?: string | null): string {
  return (row && setting(row, 'icon')) || (predicate ? RINF_ICON[predicate] : undefined) || 'record';
}

export function Chip({
  iri,
  context,
  mono,
  icon,
  label,
  href,
}: {
  iri?: string;
  context: WidgetContext;
  mono?: string;
  icon?: string;
  label?: string;
  /** A page outside DIM (a rule book paragraph): opens in a new tab. */
  href?: string;
}): JSX.Element {
  if (mono) return <span className={`${styles.chip} ${styles.chipMono}`}>{mono}</span>;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={styles.chip} data-tone={icon} title={iri ?? href}>
        {icon && <span className={styles.chipIcon} data-icon={icon} aria-hidden="true" />}
        {label ?? href} ↗
      </a>
    );
  }
  return (
    <Link to={uriToDereferencePath(iri!)} className={styles.chip} data-tone={icon} title={iri}>
      {icon && <span className={styles.chipIcon} data-icon={icon} aria-hidden="true" />}
      {label ?? uriText(iri!, context)}
    </Link>
  );
}
